import { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const MIC_CUP_ROLES = ['Player', 'Supporter', 'Coach'];
const PLAYING_POSITIONS = ['Goalkeeper', 'Defender', 'Midfielder', 'Striker'];
const FLIGHT_METHODS = ['Group Flights', 'Arranged Independently'];
const GROUP_SIZES = ['1', '2', '3', '4', '5'];
const STEPS = ['Role & Details', 'Medical', 'Travel', 'Insurance'];

const BLANK_FORM = {
  role: '',
  firstName: '', surname: '',
  passportNumber: '', nationality: '', dateOfBirth: '',
  contactPhone: '', contactEmail: '',
  shirtNumber: '', position: '',
  guardianName: '', guardianPassportNumber: '', guardianNationality: '', guardianPhone: '', guardianEmail: '',
  allergies: '', conditions: '',
  emergencyContactName: '', emergencyContactPhone: '',
  flightArrangementMethod: '', travelGroupSize: '',
  outboundFlightDetails: '', outboundFlightNumber: '',
  returnFlightDetails: '', returnFlightNumber: '',
  insuranceConfirmed: false,
};

function isPlayer(role) { return role === 'Player'; }
function isIndependent(method) { return method === 'Arranged Independently'; }

function Label({ children, required, note }) {
  return (
    <label className="block text-[13px] font-medium text-[#374151] mb-1">
      {children}
      {required && <span className="text-red-500 ml-0.5">*</span>}
      {note && <span className="text-[11px] font-normal text-[#9CA3AF] ml-1.5">({note})</span>}
    </label>
  );
}

function Input({ value, onChange, type = 'text', placeholder = '', ...rest }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
      {...rest}
    />
  );
}

function Select({ value, onChange, children }) {
  return (
    <select
      value={value} onChange={onChange}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A]"
    >
      {children}
    </select>
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1A4A8A] resize-none"
    />
  );
}

export default function MicCupParticipantRegister({ tourInfo, token }) {
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState('');
  const [step, setStep]           = useState(0);
  const [form, setForm]           = useState(BLANK_FORM);

  function set(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormError('');
  }

  function validateStep(s) {
    if (s === 0) {
      if (!form.role) return 'Please select whether you are registering as a player, supporter or coach.';
      if (!form.firstName.trim()) return 'First name is required.';
      if (!form.surname.trim()) return 'Surname is required.';
      if (!form.passportNumber.trim()) return 'Passport number is required.';
      if (!form.nationality.trim()) return 'Nationality is required.';
      if (!form.dateOfBirth) return 'Date of birth is required.';
      if (!form.contactPhone.trim()) return 'Contact number is required.';
      if (!form.contactEmail.trim()) return 'Email address is required.';
      if (isPlayer(form.role)) {
        if (!form.shirtNumber.trim()) return 'Shirt number is required.';
        if (!form.position) return 'Playing position is required.';
        if (!form.guardianName.trim()) return 'Legal guardian name is required.';
        if (!form.guardianPassportNumber.trim()) return 'Legal guardian passport number is required.';
        if (!form.guardianNationality.trim()) return 'Legal guardian nationality is required.';
        if (!form.guardianPhone.trim()) return 'Legal guardian contact number is required.';
        if (!form.guardianEmail.trim()) return 'Legal guardian email is required.';
      }
    }
    if (s === 2) {
      if (!form.flightArrangementMethod) return 'Please select how your flights are being arranged.';
      if (!form.travelGroupSize) return 'Please select the size of your travelling group.';
      if (isIndependent(form.flightArrangementMethod)) {
        if (!form.outboundFlightDetails.trim()) return 'Outbound flight details are required.';
        if (!form.outboundFlightNumber.trim()) return 'Outbound flight number is required.';
        if (!form.returnFlightDetails.trim()) return 'Return flight details are required.';
        if (!form.returnFlightNumber.trim()) return 'Return flight number is required.';
      }
    }
    if (s === 3) {
      if (!form.insuranceConfirmed) return 'You must confirm the insurance declaration.';
    }
    return null;
  }

  function goNext() {
    const err = validateStep(step);
    if (err) { setFormError(err); return; }
    setFormError('');
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goBack() {
    setFormError('');
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validateStep(3);
    if (err) { setFormError(err); return; }

    setSaving(true);
    try {
      await addDoc(collection(db, 'requests', tourInfo.requestId, 'participants'), {
        formToken: token,
        formVariant: 'mic_cup',
        travellerType:   form.role,
        firstName:       form.firstName.trim(),
        surname:         form.surname.trim(),
        passportNumber:  form.passportNumber.trim(),
        nationality:     form.nationality.trim(),
        dateOfBirth:     form.dateOfBirth,
        contactPhone:    form.contactPhone.trim(),
        contactEmail:    form.contactEmail.trim(),
        shirtNumber:     isPlayer(form.role) ? form.shirtNumber.trim() : null,
        position:        isPlayer(form.role) ? form.position : null,
        guardianName:            isPlayer(form.role) ? form.guardianName.trim() : null,
        guardianPassportNumber:  isPlayer(form.role) ? form.guardianPassportNumber.trim() : null,
        guardianNationality:     isPlayer(form.role) ? form.guardianNationality.trim() : null,
        guardianPhone:           isPlayer(form.role) ? form.guardianPhone.trim() : null,
        guardianEmail:           isPlayer(form.role) ? form.guardianEmail.trim() : null,
        allergies:       form.allergies.trim() || null,
        conditions:      form.conditions.trim() || null,
        emergencyContactName:  !isPlayer(form.role) ? (form.emergencyContactName.trim() || null) : null,
        emergencyContactPhone: !isPlayer(form.role) ? (form.emergencyContactPhone.trim() || null) : null,
        flightArrangementMethod: form.flightArrangementMethod,
        travelGroupSize:         form.travelGroupSize,
        outboundFlightDetails: isIndependent(form.flightArrangementMethod) ? form.outboundFlightDetails.trim() : null,
        outboundFlightNumber:  isIndependent(form.flightArrangementMethod) ? form.outboundFlightNumber.trim() : null,
        returnFlightDetails:   isIndependent(form.flightArrangementMethod) ? form.returnFlightDetails.trim() : null,
        returnFlightNumber:    isIndependent(form.flightArrangementMethod) ? form.returnFlightNumber.trim() : null,
        insuranceConfirmed: form.insuranceConfirmed,
        submittedAt:     serverTimestamp(),
        createdAt:       serverTimestamp(),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setFormError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function formatDate(str) {
    if (!str) return '';
    const [y, m, d] = str.split('-');
    return `${d}/${m}/${y}`;
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-[#1A7A4A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-[17px] font-bold text-[#111827] mb-2">Registration complete!</p>
          <p className="text-[13px] text-[#6B7280]">
            Your details have been saved. Please let your coach know you have completed the registration form.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
      <div className="max-w-lg mx-auto">

        {/* Header */}
        <div className="bg-[#0D2240] rounded-[12px] p-6 mb-6 text-center">
          <p className="text-white font-bold text-[18px]">MST Sports Tours</p>
          <p className="text-[#93C5FD] text-[13px] mt-1">MIC Cup Registration</p>
          {tourInfo && (
            <p className="text-[#93C5FD] text-[12px] mt-0.5">
              {tourInfo.sport} — {tourInfo.destination}
              {tourInfo.departureDate && ` · ${formatDate(tourInfo.departureDate)}`}
            </p>
          )}
        </div>

        {/* MIC Cup image */}
        <div className="rounded-[12px] overflow-hidden mb-6">
          <img src="/MIC Cup image.jpeg" alt="MIC Cup" className="w-full h-auto block" />
        </div>

        {/* Step indicator */}
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] px-5 py-4 mb-5">
          <div className="flex items-center justify-between">
            {STEPS.map((label, i) => {
              const done = i < step;
              const current = i === step;
              return (
                <div key={i} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold border-2 ${
                      done    ? 'bg-[#1A4A8A] border-[#1A4A8A] text-white' :
                      current ? 'border-[#1A4A8A] text-[#1A4A8A] bg-[#EFF6FF]' :
                                'border-[#E5E7EB] text-[#9CA3AF] bg-white'
                    }`}>
                      {done ? '✓' : i + 1}
                    </div>
                    <span className={`text-[10px] font-medium mt-1 whitespace-nowrap ${current ? 'text-[#1A4A8A]' : done ? 'text-[#374151]' : 'text-[#9CA3AF]'}`}>
                      {label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-px mx-1 mb-4 ${done ? 'bg-[#1A4A8A]' : 'bg-[#E5E7EB]'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── Step 0: Role & Details ─────────────────────────────────── */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-4">
                <p className="text-[13px] font-semibold text-[#374151]">Your Details</p>

                <div>
                  <Label required>I am registering as</Label>
                  <Select value={form.role} onChange={(e) => set('role', e.target.value)}>
                    <option value="">Select…</option>
                    {MIC_CUP_ROLES.map((r) => <option key={r}>{r}</option>)}
                  </Select>
                </div>

                <div>
                  <Label required note="as appears on passport">First name</Label>
                  <Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
                </div>
                <div>
                  <Label required note="as appears on passport">Surname</Label>
                  <Input value={form.surname} onChange={(e) => set('surname', e.target.value)} />
                </div>
                <div>
                  <Label required>Passport number</Label>
                  <Input value={form.passportNumber} onChange={(e) => set('passportNumber', e.target.value)} placeholder="e.g. 123456789" />
                </div>
                <div>
                  <Label required>Nationality</Label>
                  <Input value={form.nationality} onChange={(e) => set('nationality', e.target.value)} placeholder="e.g. British" />
                </div>
                <div>
                  <Label required>Date of birth</Label>
                  <Input type="date" value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
                </div>
                <div>
                  <Label required note="including international prefix">Contact number</Label>
                  <Input type="tel" value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} placeholder="+44 7700 000000" />
                </div>
                <div>
                  <Label required>Email address</Label>
                  <Input type="email" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} placeholder="you@example.com" />
                </div>

                {isPlayer(form.role) && (
                  <>
                    <div>
                      <Label required>Shirt number</Label>
                      <Input value={form.shirtNumber} onChange={(e) => set('shirtNumber', e.target.value)} />
                    </div>
                    <div>
                      <Label required>Playing position</Label>
                      <Select value={form.position} onChange={(e) => set('position', e.target.value)}>
                        <option value="">Select…</option>
                        {PLAYING_POSITIONS.map((p) => <option key={p}>{p}</option>)}
                      </Select>
                    </div>
                  </>
                )}
              </div>

              {isPlayer(form.role) && (
                <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-4">
                  <p className="text-[13px] font-semibold text-[#374151]">Legal Guardian Details</p>
                  <div>
                    <Label required note="as appears on passport">Legal guardian full name</Label>
                    <Input value={form.guardianName} onChange={(e) => set('guardianName', e.target.value)} />
                  </div>
                  <div>
                    <Label required>Legal guardian passport number</Label>
                    <Input value={form.guardianPassportNumber} onChange={(e) => set('guardianPassportNumber', e.target.value)} />
                  </div>
                  <div>
                    <Label required>Legal guardian nationality</Label>
                    <Input value={form.guardianNationality} onChange={(e) => set('guardianNationality', e.target.value)} />
                  </div>
                  <div>
                    <Label required note="including international prefix">Legal guardian contact number</Label>
                    <Input type="tel" value={form.guardianPhone} onChange={(e) => set('guardianPhone', e.target.value)} />
                  </div>
                  <div>
                    <Label required>Legal guardian email address</Label>
                    <Input type="email" value={form.guardianEmail} onChange={(e) => set('guardianEmail', e.target.value)} />
                  </div>
                </div>
              )}

              {!isPlayer(form.role) && form.role && (
                <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-4">
                  <p className="text-[13px] font-semibold text-[#374151]">Emergency Contact</p>
                  <p className="text-[12px] text-[#9CA3AF]">Optional — someone we can contact on your behalf in an emergency.</p>
                  <div>
                    <Label>Emergency contact name</Label>
                    <Input value={form.emergencyContactName} onChange={(e) => set('emergencyContactName', e.target.value)} />
                  </div>
                  <div>
                    <Label note="including international prefix">Emergency contact phone</Label>
                    <Input type="tel" value={form.emergencyContactPhone} onChange={(e) => set('emergencyContactPhone', e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 1: Medical ────────────────────────────────────────── */}
          {step === 1 && (
            <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-4">
              <p className="text-[13px] font-semibold text-[#374151]">Medical Information</p>
              <p className="text-[12px] text-[#9CA3AF]">Leave blank if not applicable. All information is kept confidential and used only to ensure your safety on tour.</p>
              <div>
                <Label>Allergies or dietary requirements</Label>
                <Textarea value={form.allergies} onChange={(e) => set('allergies', e.target.value)} placeholder="e.g. Peanuts, vegetarian, gluten-free…" />
              </div>
              <div>
                <Label>Medical conditions</Label>
                <Textarea value={form.conditions} onChange={(e) => set('conditions', e.target.value)} placeholder="e.g. Asthma, diabetes, epilepsy…" />
              </div>
            </div>
          )}

          {/* ── Step 2: Travel ─────────────────────────────────────────── */}
          {step === 2 && (
            <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-4">
              <p className="text-[13px] font-semibold text-[#374151]">Travel Arrangements</p>
              <div>
                <Label required>How are your flights being arranged?</Label>
                <Select value={form.flightArrangementMethod} onChange={(e) => set('flightArrangementMethod', e.target.value)}>
                  <option value="">Select…</option>
                  {FLIGHT_METHODS.map((m) => <option key={m}>{m}</option>)}
                </Select>
              </div>
              <div>
                <Label required>Size of your travelling group</Label>
                <Select value={form.travelGroupSize} onChange={(e) => set('travelGroupSize', e.target.value)}>
                  <option value="">Select…</option>
                  {GROUP_SIZES.map((n) => <option key={n}>{n}</option>)}
                </Select>
              </div>

              {isIndependent(form.flightArrangementMethod) && (
                <>
                  <div>
                    <Label required note="Departure Airport - Departure Date - Departure Time - Arrival Time">Outbound flight details</Label>
                    <Textarea value={form.outboundFlightDetails} onChange={(e) => set('outboundFlightDetails', e.target.value)} rows={2} />
                  </div>
                  <div>
                    <Label required>Outbound flight number</Label>
                    <Input value={form.outboundFlightNumber} onChange={(e) => set('outboundFlightNumber', e.target.value)} />
                  </div>
                  <div>
                    <Label required note="Departure Airport - Departure Date - Departure Time">Return flight details</Label>
                    <Textarea value={form.returnFlightDetails} onChange={(e) => set('returnFlightDetails', e.target.value)} rows={2} />
                  </div>
                  <div>
                    <Label required>Return flight number</Label>
                    <Input value={form.returnFlightNumber} onChange={(e) => set('returnFlightNumber', e.target.value)} />
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Step 3: Insurance ──────────────────────────────────────── */}
          {step === 3 && (
            <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5 space-y-5">
              <p className="text-[13px] font-semibold text-[#374151]">Insurance Declaration</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.insuranceConfirmed}
                  onChange={(e) => set('insuranceConfirmed', e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded border-[#D1D5DB] text-[#1A4A8A] flex-shrink-0 cursor-pointer"
                />
                <div>
                  <p className="text-[13px] font-semibold text-[#374151] mb-0.5">Travel Insurance Responsibility</p>
                  <p className="text-[12px] text-[#6B7280] leading-relaxed">
                    I confirm that I (or my parent/legal guardian on my behalf) understand and accept responsibility for
                    arranging valid personal travel insurance covering medical costs, theft and loss for the duration of
                    this tour.
                  </p>
                </div>
              </label>
            </div>
          )}

          {formError && (
            <p className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-[8px] px-4 py-3 mt-4">{formError}</p>
          )}

          {/* Navigation */}
          <div className="flex gap-3 mt-5">
            {step > 0 && (
              <button type="button" onClick={goBack}
                className="flex-1 py-3 border border-[#E5E7EB] text-[#374151] font-semibold text-[14px] rounded-[10px] hover:bg-[#F9FAFB] transition">
                ← Back
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={goNext}
                className="flex-1 py-3 bg-[#1A4A8A] hover:bg-[#0D2240] text-white font-semibold text-[14px] rounded-[10px] transition">
                Continue →
              </button>
            ) : (
              <button type="submit" disabled={saving}
                className="flex-1 py-3 bg-[#0D2240] hover:bg-[#1A4A8A] text-white font-semibold text-[14px] rounded-[10px] transition disabled:opacity-60">
                {saving ? 'Submitting…' : 'Submit Registration'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
